import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  InputAdornment,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import "../styles/activities.css";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Activity,
  ActivityCategory,
  Technology,
  Employee,
  CreateActivityDto,
  UpdateActivityDto,
  ActivityFilters,
} from "../types";
import { activityService } from "../services/activityService";
import ActivityModal from "../components/activities/ActivityModal";
import { ITEMS_PER_PAGE } from "../constants";

// Name cell with tooltip only if truncated
const NameCell: React.FC<{ name: string }> = ({ name }) => {
  const [ref, truncated] = useIsTruncated<HTMLSpanElement>();
  return (
    <Tooltip title={name} arrow disableHoverListener={!truncated}>
      <span
        ref={ref}
        style={{
          display: "inline-block",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
        }}
      >
        {name}
      </span>
    </Tooltip>
  );
};

// Category cell with tooltip only if truncated
const CategoryCell: React.FC<{ name: string }> = ({ name }) => {
  const [ref, truncated] = useIsTruncated<HTMLSpanElement>();
  return (
    <Tooltip title={name} arrow disableHoverListener={!truncated}>
      <span
        ref={ref}
        style={{
          display: "inline-block",
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "middle",
        }}
      >
        {name}
      </span>
    </Tooltip>
  );
};

// Point of Contact cell with tooltip only if truncated
const PointOfContactCell: React.FC<{ email: string }> = ({ email }) => {
  const [ref, truncated] = useIsTruncated<HTMLSpanElement>();
  return (
    <Tooltip title={email} arrow disableHoverListener={!truncated}>
      <span
        ref={ref}
        style={{
          display: 'inline-block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'bottom',
        }}
      >
        {email}
      </span>
    </Tooltip>
  );
};

// Utility function for duplicate check
function normalizeActivityName(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9]/g, "") // Remove all non-alphanumeric chars
    .replace(/\s+/g, "") // Remove all spaces
    .toLowerCase();
}
// Utility hook to check if text is truncated
function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      setTruncated(el.scrollWidth > el.clientWidth);
    }
  });
  return [ref, truncated] as const;
}


interface ActivitiesProps {
  sidebarOpen: boolean;
}

const Activities: React.FC<ActivitiesProps> = ({ sidebarOpen }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [categories, setCategories] = useState<ActivityCategory[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null,
  );
  const [orderBy, setOrderBy] = useState<string>("name");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [banner, setBanner] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Filters
  const [filters, setFilters] = useState<ActivityFilters>({
    categoryId: undefined,
    technologyId: undefined,
    status: undefined,
    searchTerm: "",
    pageNumber: 1,
    pageSize: ITEMS_PER_PAGE,
  });

  type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
  };

  const LocalIcon: React.FC<LocalIconProps> = ({
    src,
    alt = "",
    className,
  }) => <img src={src} alt={alt} className={className} loading="lazy" />;

  useEffect(() => {
    loadMasterData();
  }, []);
  const loadActivities = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await activityService.getActivities({
        ...filters,
        pageNumber: 1,
        pageSize: 10000, // ⭐ important
      });

      setActivities(response.items);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error("Error loading activities:", error);
      setActivities([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters.categoryId, filters.technologyId, filters.status]);
  useEffect(() => {
    loadActivities();
  }, [filters.categoryId, filters.technologyId, filters.status]);

  const loadMasterData = async () => {
    try {
      const [technologiesData, categoriesData, employeesData] =
        await Promise.all([
          activityService.getTechnologies(),
          activityService.getCategories(),
          activityService.getEmployees(),
        ]);

      setTechnologies(technologiesData);

      setCategories(
        Array.isArray(categoriesData)
          ? categoriesData
          : ((categoriesData as any)?.items ?? []),
      );
      setEmployees(employeesData);

      // Fetch full activities once to derive dropdown options independent of current server-side filters
      try {
        const allResp = await activityService.getActivities({
          pageNumber: 1,
          pageSize: 10000,
        });
        setAllActivities(allResp.items || []);
      } catch (err) {
        console.error(
          "Error loading all activities for dropdown options:",
          err,
        );
      }
    } catch (error) {
      console.error("Error loading master data:", error);
    }
  };

  const handleFilterChange = (field: keyof ActivityFilters, value: any) => {
    setFilters((prev: ActivityFilters) => ({
      ...prev,
      [field]: value === "" ? undefined : value,
      pageNumber: 1, // Reset to first page when filter changes
    }));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setFilters((prev) => ({
      ...prev,
      searchTerm: value,
    }));

    setPage(0); // ⭐ reset page index
  };

  const handleAddActivity = () => {
    setSelectedActivity(null);
    setModalOpen(true);
  };

  const handleEditActivity = (activity: Activity) => {
    setSelectedActivity(activity);
    setModalOpen(true);
  };

  const handleSaveActivity = async (
    activityData: CreateActivityDto | UpdateActivityDto,
  ) => {
    // Defensive validation: block only numbers/special chars
    const name = (activityData as any).name || "";
    if (!/[a-zA-Z]/.test(name)) {
      setBanner({
        type: "error",
        message: "Activity name cannot be only numbers or special characters",
      });
      setTimeout(() => setBanner(null), 3000);
      return;
    }
    // Duplicate check (normalize for spaces, slashes, case, etc.)
    const normalizedInput = normalizeActivityName(name);
    const duplicate = activities.some(a => {
      const existing = normalizeActivityName(a.name ?? "");
      // If editing, skip self
      if ((activityData as any).id && a.id === (activityData as any).id) return false;
      return existing === normalizedInput;
    });
    if (duplicate) {
      setBanner({
        type: "error",
        message: "Activity already exists",
      });
      setTimeout(() => setBanner(null), 3000);
      return;
    }
    try {
      if ("id" in activityData) {
        await activityService.updateActivity(activityData.id, activityData);
        setBanner({
          type: "success",
          message: "Activity updated successfully",
        });
        await loadActivities();
      } else {
        // Create and immediately show new activity at the top
        const created = await activityService.createActivity(activityData);
        setBanner({
          type: "success",
          message: "Activity added successfully",
        });
        // If API returns the new activity, prepend it; else reload
        if (created && created.id) {
          setActivities((prev) => [{ ...created }, ...prev]);
          setOrderBy("createdDate");
          setOrder("desc");
          setFilters({
            categoryId: undefined,
            technologyId: undefined,
            status: undefined,
            searchTerm: "",
            pageNumber: 1,
            pageSize: ITEMS_PER_PAGE
          }); // Clear filters
          setPage(0); // Reset to first page after adding new activity
        } else {
          await loadActivities();
          setOrderBy("createdDate");
          setOrder("desc");
        }
      }
      setModalOpen(false);
      setTimeout(() => setBanner(null), 3000);
    } catch (error) {
      console.error("Error saving activity:", error);
      setBanner({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
      setTimeout(() => setBanner(null), 3000);
    }
  };

  const getCategoryName = (categoryId: number): string => {
    return categories.find((c) => c.id === categoryId)?.name || "";
  };

  const getTechnologyName = (technologyId: number): string => {
    return technologies.find((t) => t.id === technologyId)?.name || "";
  };

  const getEmployeeEmail = (employeeId: number): string => {
    return employees.find((e) => e.id === employeeId)?.email || "";
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getFieldValue = React.useCallback(
    (activity: Activity, property: string): any => {
      switch (property) {
        case "name":
          return activity.name || "";
        case "category":
          return (
            categories.find((c) => c.id === activity.categoryId)?.name || ""
          );
        case "technology":
          return (
            technologies.find((t) => t.id === activity.technologyId)?.name || ""
          );
        case "description":
          return activity.description || "";
        case "pointOfContact":
          return (
            employees.find((e) => e.id === activity.pointOfContactId)?.email ||
            employees.find((e) => e.id === activity.pointOfContactId)?.name ||
            ""
          );
        case "status":
          return activity.status || "";
        default:
          return "";
      }
    },
    [categories, technologies, employees],
  );

  // Always show newest activities at the top by createdDate desc unless user sorts by another column
  const sortedActivities = React.useMemo(() => {
    const arr = [...activities];
    if (!orderBy) {
      arr.sort((a, b) => {
        const dateA = new Date(a.createdDate ?? 0).getTime();
        const dateB = new Date(b.createdDate ?? 0).getTime();
        return dateB - dateA;
      });
    } else {
      arr.sort((a, b) => {
        let va = getFieldValue(a, orderBy);
        let vb = getFieldValue(b, orderBy);
        // Special handling for Name column
        if (orderBy === "name") {
          va = a.name || "";
          vb = b.name || "";
        }
        if (typeof va === "number" && typeof vb === "number") {
          return order === "asc" ? va - vb : vb - va;
        }
        const sa = String(va ?? "").toLowerCase();
        const sb = String(vb ?? "").toLowerCase();
        if (sa < sb) return order === "asc" ? -1 : 1;
        if (sa > sb) return order === "asc" ? 1 : -1;
        return 0;
      });
    }
    return arr;
  }, [activities, orderBy, order, getFieldValue]);
  const filteredActivities = React.useMemo(() => {
    if (!filters.searchTerm) return sortedActivities;

    const term = filters.searchTerm.toLowerCase();

    return sortedActivities.filter(
      (a) =>
        getCategoryName(a.categoryId).toLowerCase().includes(term) ||
        getTechnologyName(a.technologyId).toLowerCase().includes(term) ||
        (a.name || "").toLowerCase().includes(term) ||
        (a.description || "").toLowerCase().includes(term) ||
        getEmployeeEmail(a.pointOfContactId).toLowerCase().includes(term) ||
        (a.status || "").toLowerCase().includes(term) ||
        formatDate(a.createdDate).toLowerCase().includes(term) ||
        formatDate(a.modifiedDate).toLowerCase().includes(term),
    );
  }, [
    sortedActivities,
    filters.searchTerm,
    categories,
    technologies,
    employees,
  ]);

  useEffect(() => {
    setPage(0);
  }, [filters.searchTerm]);
  const paginatedFilteredData = React.useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredActivities.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredActivities, page]);

  // Derive visible filter options from the full activities list but ignore the currently-selected filter
  const matchesSearch = React.useCallback(
    (a: Activity, term?: string) => {
      if (!term) return true;
      const t = term.toLowerCase();
      return (
        getCategoryName(a.categoryId).toLowerCase().includes(t) ||
        getTechnologyName(a.technologyId).toLowerCase().includes(t) ||
        (a.name || "").toLowerCase().includes(t) ||
        (a.description || "").toLowerCase().includes(t) ||
        getEmployeeEmail(a.pointOfContactId).toLowerCase().includes(t) ||
        (a.status || "").toLowerCase().includes(t) ||
        formatDate(a.createdDate).toLowerCase().includes(t) ||
        formatDate(a.modifiedDate).toLowerCase().includes(t)
      );
    },
    [categories, technologies, employees],
  );

  const visibleCategoryOptions = React.useMemo(() => {
    const acts = allActivities.filter((a) => {
      if (filters.technologyId && a.technologyId !== filters.technologyId)
        return false;
      if (filters.status && a.status !== filters.status) return false;
      if (filters.searchTerm && !matchesSearch(a, filters.searchTerm))
        return false;
      return true; // NOTE: ignore filters.categoryId so dropdown shows all categories present given other filters
    });
    const ids = Array.from(
      new Set(
        acts
          .map((a) => a.categoryId)
          .filter((id) => id !== undefined && id !== null),
      ),
    );
    return ids
      .map((id) => categories.find((c) => c.id === id))
      .filter((c): c is ActivityCategory => !!c)
      .sort((x, y) =>
        (x.name || "")
          .toLowerCase()
          .localeCompare((y.name || "").toLowerCase()),
      );
  }, [
    allActivities,
    categories,
    filters.technologyId,
    filters.status,
    filters.searchTerm,
    matchesSearch,
  ]);

  const visibleTechnologyOptions = React.useMemo(() => {
    const acts = allActivities.filter((a) => {
      if (filters.categoryId && a.categoryId !== filters.categoryId)
        return false;
      if (filters.status && a.status !== filters.status) return false;
      if (filters.searchTerm && !matchesSearch(a, filters.searchTerm))
        return false;
      return true; // ignore filters.technologyId
    });
    const ids = Array.from(
      new Set(
        acts
          .map((a) => a.technologyId)
          .filter((id) => id !== undefined && id !== null),
      ),
    );
    return ids
      .map((id) => technologies.find((t) => t.id === id))
      .filter((t): t is Technology => !!t)
      .sort((x, y) =>
        (x.name || "")
          .toLowerCase()
          .localeCompare((y.name || "").toLowerCase()),
      );
  }, [
    allActivities,
    technologies,
    filters.categoryId,
    filters.status,
    filters.searchTerm,
    matchesSearch,
  ]);

  const visibleStatusOptions = React.useMemo(() => {
    const acts = allActivities.filter((a) => {
      if (filters.categoryId && a.categoryId !== filters.categoryId)
        return false;
      if (filters.technologyId && a.technologyId !== filters.technologyId)
        return false;
      if (filters.searchTerm && !matchesSearch(a, filters.searchTerm))
        return false;
      return true; // ignore filters.status
    });
    const statuses = Array.from(
      new Set(acts.map((a) => a.status).filter((s) => !!s)),
    ) as string[];
    return statuses.sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [
    allActivities,
    filters.categoryId,
    filters.technologyId,
    filters.searchTerm,
    matchesSearch,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredActivities.length / ITEMS_PER_PAGE),
  );

  const isPrevDisabled = page === 0;
  const isNextDisabled = page >= totalPages - 1;
  return (
    <div className="activities-container activities-layout">
      {/* Top Section */}
      <div className="activities-top">
        <h2 className="categories-heading">Activities</h2>

        <div className="activities-controls">
          <Button
            variant="contained"
            onClick={handleAddActivity}
            size="small"
            className="btn-primary"
          >
            <LocalIcon src="/icons/AddIcon.svg" className="btn-icon" />
            Add Activity
          </Button>

          <Autocomplete
            className="filter-autocomplete"
            options={visibleCategoryOptions}
            getOptionLabel={(option) => option.name}
            value={categories.find((c) => c.id === filters.categoryId) || null}
            onChange={(_, newValue) =>
              handleFilterChange(
                "categoryId",
                newValue ? newValue.id : undefined,
              )
            }
            size="small"
            clearOnEscape
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Category"
                variant="outlined"
                className="filter-input"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <>{params.InputProps.startAdornment}</>,
                }}
              />
            )}
          />

          <Autocomplete
            className="filter-autocomplete"
            options={visibleTechnologyOptions}
            getOptionLabel={(option) => option.name}
            value={
              technologies.find((t) => t.id === filters.technologyId) || null
            }
            onChange={(_, newValue) =>
              handleFilterChange(
                "technologyId",
                newValue ? newValue.id : undefined,
              )
            }
            size="small"
            clearOnEscape
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Technology"
                variant="outlined"
                className="filter-input"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <>{params.InputProps.startAdornment}</>,
                }}
              />
            )}
          />

          <Autocomplete
            className="filter-autocomplete status-filter"
            options={
              visibleStatusOptions.length
                ? visibleStatusOptions
                : ["Active", "Inactive"]
            }
            value={filters.status || null}
            onChange={(_, newValue) =>
              handleFilterChange("status", newValue || undefined)
            }
            size="small"
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Status"
                variant="outlined"
                className="filter-input"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <>{params.InputProps.startAdornment}</>,
                }}
              />
            )}
          />

          <TextField
            size="small"
            placeholder="Search"
            value={filters.searchTerm}
            onChange={handleSearchChange}
            className="filter-search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="search-icon" />
                </InputAdornment>
              ),
              endAdornment: filters.searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() =>
                      setFilters((f) => ({ ...f, searchTerm: "" }))
                    }
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="table-card">
        <TableContainer className="activities-table-container">
          <Table className="activities-table">
            <TableHead>
              <TableRow className="activities-table-row">
                {[
                  { key: "name", label: "Name" },
                  { key: "category", label: "Category" },
                  { key: "technology", label: "Technology" },
                ].map((column) => (
                  <TableCell
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="activities-th sortable"
                  >
                    <div className="th-inner">
                      {column.label}
                      <div className="sort-icons">
                        <ArrowUpwardIcon
                          className={`sort-icon ${
                            orderBy === column.key && order === "asc"
                              ? "active"
                              : ""
                          }`}
                        />
                        <ArrowDownwardIcon
                          className={`sort-icon ${
                            orderBy === column.key && order === "desc"
                              ? "active"
                              : ""
                          }`}
                        />
                      </div>
                    </div>
                  </TableCell>
                ))}


                <TableCell
                  onClick={() => handleSort("description")}
                  className="activities-th sortable"
                >
                  <div className="th-inner">
                    Description
                    <div className="sort-icons">
                      <ArrowUpwardIcon
                        className={`sort-icon ${orderBy === "description" && order === "asc" ? "active" : ""}`}
                      />
                      <ArrowDownwardIcon
                        className={`sort-icon ${orderBy === "description" && order === "desc" ? "active" : ""}`}
                      />
                    </div>
                  </div>
                </TableCell>

                <TableCell
                  onClick={() => handleSort("pointOfContact")}
                  className="activities-th sortable"
                >
                  <div className="th-inner">
                    Point of Contact
                    <div className="sort-icons">
                      <ArrowUpwardIcon
                        className={`sort-icon ${orderBy === "pointOfContact" && order === "asc" ? "active" : ""}`}
                      />
                      <ArrowDownwardIcon
                        className={`sort-icon ${orderBy === "pointOfContact" && order === "desc" ? "active" : ""}`}
                      />
                    </div>
                  </div>
                </TableCell>

                <TableCell
                  onClick={() => handleSort("status")}
                  className="activities-th sortable"
                >
                  <div className="th-inner">
                    Status
                    <div className="sort-icons">
                      <ArrowUpwardIcon
                        className={`sort-icon ${
                          orderBy === "status" && order === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <ArrowDownwardIcon
                        className={`sort-icon ${
                          orderBy === "status" && order === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </div>
                  </div>
                </TableCell>

                <TableCell align="center" className="activities-th">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? null : filteredActivities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" className="empty-state">
                    <div className="empty-content">
                      <span className="th-inner">No Activities found.<br/>
                      Try a different search or add a new Activity.</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedFilteredData.map((activity) => (
                  <TableRow key={activity.id} hover>
                    <TableCell>
                      <NameCell name={activity.name || ""} />
                    </TableCell>
                    <TableCell>
                      <CategoryCell name={getCategoryName(activity.categoryId)} />
                    </TableCell>
                    <TableCell>
                      {getTechnologyName(activity.technologyId) &&
                      getTechnologyName(activity.technologyId).length > 20 ? (
                        <Tooltip
                          title={getTechnologyName(activity.technologyId)}
                          arrow
                        >
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {getTechnologyName(activity.technologyId)}
                          </span>
                        </Tooltip>
                      ) : (
                        <span
                          style={{
                            display: "inline-block",
                            maxWidth: 120,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                          }}
                        >
                          {getTechnologyName(activity.technologyId)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.description &&
                      activity.description.length > 40 ? (
                        <Tooltip title={activity.description} arrow>
                          <span
                            className="description-cell"
                            style={{
                              display: "inline-block",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                            }}
                          >
                            {activity.description}
                          </span>
                        </Tooltip>
                      ) : (
                        <span
                          className="description-cell"
                          style={{
                            display: "inline-block",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                          }}
                        >
                          {activity.description}
                        </span>
                      )}
                    </TableCell>

                    <TableCell>
                      <PointOfContactCell email={getEmployeeEmail(activity.pointOfContactId)} />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={activity.status}
                        size="small"
                        className={
                          activity.status === "Active"
                            ? "badge badge-active"
                            : "badge badge-inactive"
                        }
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit Activity" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleEditActivity(activity)}
                          className="edit-btn"
                          tabIndex={0}
                        >
                          <LocalIcon
                            src="/icons/EditIcon.svg"
                            className="edit-icon"
                          />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

            {/* Pagination */}
          <div className="activities-pagination">

            <div className="pagination-info">
                Showing{" "}
                {filteredActivities.length === 0
                    ? 0
                    : page * ITEMS_PER_PAGE + 1}{" "}
                to{" "}
                {Math.min((page + 1) * ITEMS_PER_PAGE, filteredActivities.length)}{" "}
                of {filteredActivities.length} entries | Page {page + 1} of {totalPages}
            </div>

                <button
                    className="page-btn prev-btn"
                    disabled={isPrevDisabled}
                    onClick={() => {
                        const newPage = Math.max(0, page - 1);
                        setPage(newPage);
                        setFilters(prev => ({
                            ...prev,
                            pageNumber: newPage + 1,
                        }));
                    }}
                >
                    Previous
                </button>

        <div className="page-numbers">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`page-number ${p === page + 1 ? "active" : ""}`}
              onClick={() => {
                setPage(p - 1);
                setFilters((prev) => ({
                  ...prev,
                  pageNumber: p,
                }));
              }}
            >
              {p}
            </button>
          ))}
        </div>

        <button
          className="page-btn next-btn"
          disabled={isNextDisabled}
          onClick={() => {
            const newPage = Math.min(totalPages - 1, page + 1);
            setPage(newPage);
            setFilters((prev) => ({
              ...prev,
              pageNumber: newPage + 1,
            }));
          }}
        >
          Next
        </button>
      </div>

      {/* Modal */}
      <ActivityModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveActivity}
        activity={selectedActivity}
        activities={activities}
        categories={categories}
        technologies={technologies}
        employees={employees}
      />

      {banner && (
        <div className={`toast-banner ${banner.type}`}>{banner.message}</div>
      )}
    </div>
  );
};

export default Activities;
